import React, { useState, useEffect, useCallback } from 'react';
import { BellOutlined } from '@ant-design/icons';
import { Modal, Badge, Spin, Empty } from 'antd';
import '../../../Assets/styles/LandingStyles/Promotions.css'
import { useLanguage } from '../../../Core/Translations/LanguageContext';

// Interface for the processed promotion data (localized)
interface Promotion {
    id: string;
    title: string;
    description: string;
    requirements: string;
    prizeImg: string | null;
    active: boolean;
}

// Interface for the raw row data fetched from the CSV (all languages included)
interface PromotionRow {
    id: string;
    title_en: string;
    title_es: string;
    title_fr: string;
    title_hi: string;
    title_bn: string;
    title_pt: string;
    title_it: string;
    title_ru: string;
    title_ja: string;
    title_ko: string;
    description_en: string;
    description_es: string;
    description_fr: string;
    description_hi: string;
    description_bn: string;
    description_pt: string;
    description_it: string;
    description_ru: string;
    description_ja: string;
    description_ko: string;
    requirements_en: string;
    requirements_es: string;
    requirements_fr: string;
    requirements_hi: string;
    requirements_bn: string;
    requirements_pt: string;
    requirements_it: string;
    requirements_ru: string;
    requirements_ja: string;
    requirements_ko: string;
    reward: string;
    startDate: string;
    endDate: string;
    maxClaims: string;
    active: string;
    priority: string;
    prize_img?: string;
}

const CSV_REGEX = /(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|([^,]*))(?:,|$)/g;

const parseCSV = (csvText: string): PromotionRow[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const rows: PromotionRow[] = [];

    for (const line of lines.slice(1)) {
        const row: any = {};
        let match;
        let columnIndex = 0;

        CSV_REGEX.lastIndex = 0;

        while ((match = CSV_REGEX.exec(line))) {
            let value = match[1] !== undefined ? match[1].replace(/""/g, '"') : match[2];
            value = (value || '').trim();

            if (columnIndex < headers.length) {
                row[headers[columnIndex].trim()] = value;
            }
            columnIndex++;

            if (CSV_REGEX.lastIndex >= line.length && columnIndex >= headers.length) {
                break;
            }
            if (match[0].length === 0 && CSV_REGEX.lastIndex < line.length) {
                CSV_REGEX.lastIndex++;
            }
        }

        if (Object.keys(row).length === headers.length) {
            rows.push(row as PromotionRow);
        } else {
            console.warn("Skipping malformed CSV row (missing fields):", line, row);
        }
    }
    return rows;
};

const PromotionsNotification: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [loading, setLoading] = useState(false);
    const [unreadPromotions, setUnreadPromotions] = useState<string[]>([]);

    const { language } = useLanguage();

    const CSV_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQ9vpfuFSh26qS8LVYywX3GGYD9reJc6JH3jWBf5g2BwFiryq5KUb6OGvquHSHGXbVABzJwYHtyHLw8/pub?gid=0&single=true&output=csv';

    const getViewedPromotionIds = useCallback((): string[] => {
        const viewedPromotions = localStorage.getItem('viewedPromotions');
        return viewedPromotions ? JSON.parse(viewedPromotions) : [];
    }, []);

    const markPromotionAsRead = useCallback((id: string) => {
        setUnreadPromotions(prevUnread => {
            if (!prevUnread.includes(id)) {
                return prevUnread;
            }

            const newUnread = prevUnread.filter(promoId => promoId !== id);
            
            const displayedPromotionIds = promotions.map(p => p.id);
            const isDisplayed = displayedPromotionIds.includes(id);

            if (isDisplayed) {
                const viewedIds = getViewedPromotionIds();
                if (!viewedIds.includes(id)) {
                    localStorage.setItem('viewedPromotions', JSON.stringify([...viewedIds, id]));
                }
            }

            return newUnread;
        });
    }, [getViewedPromotionIds, promotions]);

    const fetchPromotions = useCallback(async () => {
        setLoading(true);
        const maxRetries = 3;
        let attempt = 0;

        while (attempt < maxRetries) {
            try {
                const response = await fetch(CSV_URL);
                if (!response.ok) throw new Error('Network response was not ok');

                const csvText = await response.text();
                const rows = parseCSV(csvText);

                const langCode = language || 'en';
                const viewedIds = getViewedPromotionIds();

                const activePromotions = rows
                    .filter(row => row.active?.toLowerCase() === 'true')
                    .map(row => {
                        const titleKey = `title_${langCode}` as keyof PromotionRow;
                        const descriptionKey = `description_${langCode}` as keyof PromotionRow;
                        const requirementsKey = `requirements_${langCode}` as keyof PromotionRow;

                        // Handle prize_img: if it's "null" string or empty, set to null
                        const prizeImg = row.prize_img && row.prize_img.toLowerCase() !== 'null' && row.prize_img.trim() !== '' ? row.prize_img.trim() : null;

                        return {
                            id: row.id,
                            title: row[titleKey] || row.title_en,
                            description: row[descriptionKey] || row.description_en,
                            requirements: row[requirementsKey] || row.requirements_en,
                            prizeImg,
                            active: true
                        };
                    });

                setPromotions(activePromotions);

                const unreadIds = activePromotions
                    .map(promo => promo.id)
                    .filter(id => !viewedIds.includes(id));

                setUnreadPromotions(unreadIds);

                setLoading(false);
                return;

            } catch (error) {
                console.error(`Attempt ${attempt + 1}: Error fetching or parsing promotions:`, error);
                attempt++;
                if (attempt < maxRetries) {
                    const delay = Math.pow(2, attempt) * 1000;
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }
        setLoading(false);
    }, [language, getViewedPromotionIds]);

    useEffect(() => {
        fetchPromotions();
    }, [fetchPromotions]);

    const handleBellClick = () => {
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
    };

    const handlePromotionHover = (id: string) => {
        if (isModalVisible) {
            markPromotionAsRead(id);
        }
    };

    const getModalTitle = (lang: string) => {
        switch (lang) {
            case 'es': return 'Promociones Activas';
            case 'fr': return 'Promotions Actives';
            case 'hi': return 'सक्रिय प्रचार';
            case 'bn': return 'সক্রিয় প্রচার';
            case 'pt': return 'Promoções Ativas';
            case 'it': return 'Promozioni Attive';
            case 'ru': return 'Активные Акции';
            case 'ja': return 'アクティブプロモーション';
            case 'ko': return '활성 프로모션';
            default: return 'Active Promotions';
        }
    };

    const getEmptyDescription = (lang: string) => {
        switch (lang) {
            case 'es': return 'No hay promociones activas';
            case 'fr': return 'Aucune promotion active';
            case 'hi': return 'कोई सक्रिय प्रचार नहीं';
            case 'bn': return 'কোন সক্রিয় প্রচার নেই';
            case 'pt': return 'Nenhuma promoção ativa';
            case 'it': return 'Nessuna promozione attiva';
            case 'ru': return 'Нет активных акций';
            case 'ja': return 'アクティブなプロモーションはありません';
            case 'ko': return '활성 프로모션 없음';
            default: return 'No active promotions';
        }
    };

    const getRequirementsLabel = (lang: string) => {
        switch (lang) {
            case 'es': return 'Requisitos: ';
            case 'fr': return 'Exigences: ';
            case 'hi': return 'आवश्यकताएँ: ';
            case 'bn': return 'আবশ্যকতা: ';
            case 'pt': return 'Requisitos: ';
            case 'it': return 'Requisiti: ';
            case 'ru': return 'Требования: ';
            case 'ja': return '要件: ';
            case 'ko': return '요구사항: ';
            default: return 'Requirements: ';
        }
    };

    const isMainBellUnread = unreadPromotions.length > 0;

    return (
        <>
            <div className="promotions-bell-component">
                <div className="flex justify-start p-4 bg-gray-50 border-b border-gray-200">
                    <div className="promotions-bell-container">
                        <Badge count={isMainBellUnread ? 1 : 0} dot offset={[-5, 5]}>
                            <BellOutlined
                                className="promotions-bell-icon"
                                onClick={handleBellClick}
                            />
                        </Badge>
                    </div>
                </div>

                <Modal
                    title={getModalTitle(language)}
                    open={isModalVisible}
                    onCancel={handleModalClose}
                    footer={null}
                    width={600}
                    className="promotions-modal"
                >
                    {loading ? (
                        <div className="promotions-loading">
                            <Spin size="large" />
                        </div>
                    ) : promotions.length === 0 ? (
                        <Empty
                            description={getEmptyDescription(language)}
                        />
                    ) : (
                        <div className="promotions-list">
                            {promotions.map((promotion) => (
                                <div
                                    key={promotion.id}
                                    className="promotion-card-wrapper"
                                    onMouseEnter={() => handlePromotionHover(promotion.id)}
                                >
                                    <Badge
                                        dot
                                        count={unreadPromotions.includes(promotion.id) ? 1 : 0}
                                        className="promotion-dot-badge"
                                        offset={[0, 0]}
                                    >
                                        <div className="promotion-card">
                                            {promotion.prizeImg && (
                                                <div className="promotion-prize-image-container">
                                                    <img
                                                        src={promotion.prizeImg}
                                                        alt={`${promotion.title} prize`}
                                                        className="promotion-prize-image"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <h3 className="promotion-title">{promotion.title}</h3>
                                            <p className="promotion-description">{promotion.description}</p>
                                            {promotion.requirements && (
                                                <div className="promotion-requirements">
                                                    <strong>
                                                        {getRequirementsLabel(language)}
                                                    </strong>
                                                    <span style={{ whiteSpace: 'pre-wrap' }}>
                                                        {promotion.requirements.replace(/ \| /g, '\n')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default PromotionsNotification;
